import { useMaintenanceStatus } from '@/api/hooks/status';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MAINTENANCE_COPY, type MaintenanceVariant } from '@/lib/maintenance-copy';
import { Wrench } from 'lucide-react';
import { useEffect } from 'react';
import type * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Maintenance lockout page. Rendered by `RootGate` when the
 * `maintenance-mode` flag is on and the caller lacks `maintenance:bypass`.
 *
 * Copy variants ship from `lib/maintenance-copy.ts` so the page stays
 * purely presentational and the 3-variant Storybook matrix consumes the
 * same constants the runtime page does.
 *
 * The "Admin sign-in" footer link points at `/auth/login` — only admins who
 * hold `maintenance:bypass` (seeded onto `super_admin` only) can sign in
 * and reach the admin Flags tab to flip the kill-switch off. Non-admins
 * hitting the link will sign in and be bounced straight back here by
 * `RootGate`, which is the documented behaviour.
 *
 * Auto-recovery: the page polls `useMaintenanceStatus` (30s interval per
 * the hook's `refetchInterval`) and `navigate('/')` once the flag flips
 * off. Without this, `RootGate` would never re-evaluate for visitors
 * stranded on `/maintenance` — `UNAUTH_SURFACES` short-circuits the gate
 * to `<Outlet/>` for this path, so the parent never fires the redirect
 * back to `/`. Test substrate omits the hook when no QueryClient is
 * mounted; the `isError` short-circuit keeps the page rendering then.
 */
export interface MaintenanceProps {
  variant?: MaintenanceVariant;
}

export function Maintenance({ variant = 'default' }: MaintenanceProps): React.ReactElement {
  const { heading, body } = MAINTENANCE_COPY[variant];
  const { maintenance, isInitialLoad, isError } = useMaintenanceStatus();
  const navigate = useNavigate();

  useEffect(() => {
    // Recover only on a confirmed off-state. Both `isInitialLoad` (first
    // settle not yet reached) and `isError` leave `maintenance` at its
    // fail-open `false` default (posture documented in `hooks/status.ts`), so
    // gating on them stops the page from bouncing visitors out before the
    // first response lands or on a transient `/api/status` 5xx.
    if (!isInitialLoad && !isError && !maintenance) {
      navigate('/', { replace: true });
    }
  }, [maintenance, isInitialLoad, isError, navigate]);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-muted/30 px-6 text-foreground">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Wrench className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="mt-3">{heading}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="pt-0 text-center">
          <p className="text-xs text-muted-foreground">
            This page recovers automatically once maintenance ends.
          </p>
        </CardContent>
      </Card>
      <Link to="/auth/login" className="text-xs text-muted-foreground hover:text-foreground">
        Admin sign-in
      </Link>
    </main>
  );
}
